import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import { Container, Row, Col } from 'reactstrap';
import { isObject, isArray } from 'lodash';

import {
  usePage,
  useTealium,
  useAdGlare,
  useUnmountResetPage,
  useDebounceLoading,
  useDelayJsLoadiing,
} from '../../utils/hookHelper';

import store from '../../redux/store';
import HelmetMeta from '../../components/HelmetMeta';
import HelmetTitle from '../../components/HelmetTitle';
import HelmetStyle from '../../components/HelmetStyle';
import HelmetScript from '../../components/HelmetScript';
import HelmetHead from '../../components/HelmetHead';
import Loader from '../../components/Loader';
import UnderConstruction from '../../components/UnderConstruction';
import SearchInput from '../../widgets/SearchInput';
import { parseSanitizedHTML } from '../../utils/Helper';
import {
  getWidgetArticleList,
  resetWidgetArticleList,
} from '../../redux/modules/widgetArticleList/action';
import config from '../../config';

// components
const Jumbotron = loadable(() => import('../../widgets/Jumbotron'));
const Cards = loadable(() => import('../../widgets/Cards'));
const ArticleSlider = loadable(() => import('../../widgets/ArticleSlider'));

const Home = (props) => {
  const {
    i18n,
    homeData,
    allowCookies,
    enableTealium,
    enableAdGlare,
    siteConfig,
    widgetArticleSlider,
  } = props;
  const debouncePageLoading = useDebounceLoading(homeData);
  const jsLoading = useDelayJsLoadiing(homeData);

  usePage('home', config.pageType.homePage);
  useAdGlare(i18n.locale, siteConfig, homeData, enableAdGlare.status);
  useUnmountResetPage();

  const siteConfigData = siteConfig.data || {};
  const {
    defaultLanguage,
    searchbar = false,
    searchbar_projectCode = 'hktdc',
    searchbar_projectName = 'hktdc',
    searchbar_itemPerPage = 20,
    searchbar_viewType = 'list',
  } = siteConfigData;

  const { translations = {}, locale = defaultLanguage } = i18n || {};
  const t = translations[locale] || {};
  const { data = {} } = homeData || {};
  const content = ((data || {}).content || {}).content || {};
  const dataContent = content[locale] || {};
  const { under_construction = false } = content;

  // content
  const { jumbotron = {}, main_paragraph1 = '', main_paragraph2 = '', cards = {}, mtConfig = {} } =
    dataContent || {};

  // HelmetMeta
  const { metadata = {} } = data || {};
  const publishDate = metadata['TeamSite/LiveSite/PublishDate'];
  const seo = dataContent.seo || {};
  const {
    css_style_block = '',
    js_script_block = '',
    extra_head = '',
    showSliderWidget = false,
    sliderWidgetType = '',
    sliderWidgetQuery = '',
  } = content;

  const title = seo.seo_title;
  const [domTitle, setDomTitle] = useState('');

  useTealium(
    i18n.locale,
    siteConfig,
    homeData,
    allowCookies.status,
    enableTealium.status,
    domTitle,
  );

  const helmetTitleCallBack = (newState) => {
    setDomTitle(newState.title);
  };

  const middleContent1 = main_paragraph1.length > 0 && main_paragraph1 !== '' && (
    <section className="section-mainPage-middleContent1">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_paragraph1)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );
  const middleContent2 = main_paragraph2.length > 0 && main_paragraph2 !== '' && (
    <section className="section-mainPage-middleContent2">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_paragraph2)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );

  const { homeCardCustomBodyInd = false } = siteConfigData;

  const onSearchSubmit = (value) => {
    window.open(
      `${config.eventPath}/fair/${searchbar_projectCode}-${locale}/Exhibitors-Search/${searchbar_projectName}/${value}/?page=1&pageItem=${searchbar_itemPerPage}&view=${searchbar_viewType}`,
    );
  };

  const [articleSlider, setArticleSlider] = useState(false);
  useEffect(() => {
    if (articleSlider === false && showSliderWidget && sliderWidgetQuery !== '') {
      setArticleSlider(true);
      store.dispatch(getWidgetArticleList(sliderWidgetQuery, 0, 6, sliderWidgetType));
    }
  }, [homeData]);

  const { lists = {} } = widgetArticleSlider || {};

  const slidersComponent = lists.length > 0 && (
    <ArticleSlider lists={lists} title={mtConfig.sliderWidgetTitle} locale={locale} t={t} />
  );

  const messageCards = (cards) => {
    if (isArray(cards)) {
      return cards;
    }
    if (isObject(cards) && !Object.values(cards).every((x) => x === null || x === '')) {
      return [cards];
    }
    return null;
  };

  const mainContent = (
    <>
      <Jumbotron banners={jumbotron} />
      {searchbar && (
        <div className="searchInputWrapper">
          <SearchInput onSubmit={onSearchSubmit} />
        </div>
      )}
      {middleContent1}
      {slidersComponent}
      <section className="section-mainPage-Cards">
        {messageCards(cards) && (
          <Container>
            <Cards cards={messageCards(cards)} homeCardCustomBodyInd={homeCardCustomBodyInd} />
          </Container>
        )}
      </section>
      {middleContent2}
    </>
  );

  const pageEle = (
    <div className="mainPage">
      {under_construction && <UnderConstruction />}
      <h1 className="access-elem">{seo.seo_title}</h1>
      {!under_construction && mainContent}
      {!under_construction && <HelmetScript extraHead={extra_head} jsScript={js_script_block} />}
    </div>
  );

  return (
    <>
      {debouncePageLoading || jsLoading ? <Loader /> : pageEle}
      {!debouncePageLoading && <HelmetHead extraHead={extra_head} />}
      {!debouncePageLoading && <HelmetStyle stylesheet={css_style_block} />}
      {!debouncePageLoading && <HelmetTitle title={title} cb={helmetTitleCallBack} />}
      {!debouncePageLoading && (
        <HelmetMeta seo={seo} publishDate={publishDate} pageClass="homePage" />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  i18n: state.i18n,
  homeData: state.generalPage,
  allowCookies: state.allowCookies,
  enableTealium: state.enableTealium,
  enableAdGlare: state.enableAdGlare,
  siteConfig: state.siteConfig,
  widgetArticleSlider: state.widgetArticleList,
});

const connectStore = connect(mapStateToProps, null, null, {
  pure: true,
})(Home);

export { Home as HomeComponent }; // <- For component Unit Test
export default connectStore;
